import { toast as sonnerToast } from "sonner"

interface ToastOptions {
  description?: string
  duration?: number
}

const SuccessIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
  </svg>
)

const ErrorIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
      clipRule="evenodd"
    />
  </svg>
)

const WarningIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
)

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
    <path
      fillRule="evenodd"
      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
      clipRule="evenodd"
    />
  </svg>
)

export const toast = {
  success: (message: string, options?: ToastOptions) => {
    return sonnerToast.custom(
      t => (
        <div className="bg-green-500 text-white px-4 py-3 rounded-lg shadow-lg">
          {options?.description ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <SuccessIcon />
                  <span className="font-semibold text-sm">{message}</span>
                </div>
                <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                  <CloseIcon />
                </button>
              </div>
              <p className="text-xs text-white/90 ml-6">{options.description}</p>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <SuccessIcon />
                <span className="text-sm">{message}</span>
              </div>
              <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                <CloseIcon />
              </button>
            </div>
          )}
        </div>
      ),
      { duration: options?.duration }
    )
  },

  error: (message: string, options?: ToastOptions) => {
    return sonnerToast.custom(
      t => (
        <div className="bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg">
          {options?.description ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <ErrorIcon />
                  <span className="font-semibold text-sm">{message}</span>
                </div>
                <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                  <CloseIcon />
                </button>
              </div>
              <p className="text-xs text-white/90 ml-6">{options.description}</p>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ErrorIcon />
                <span className="text-sm">{message}</span>
              </div>
              <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                <CloseIcon />
              </button>
            </div>
          )}
        </div>
      ),
      { duration: options?.duration }
    )
  },

  warning: (message: string, options?: ToastOptions) => {
    return sonnerToast.custom(
      t => (
        <div className="bg-orange-500 text-white px-4 py-3 rounded-lg shadow-lg">
          {options?.description ? (
            <>
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <WarningIcon />
                  <span className="font-semibold text-sm">{message}</span>
                </div>
                <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                  <CloseIcon />
                </button>
              </div>
              <p className="text-xs text-white/90 ml-6">{options.description}</p>
            </>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <WarningIcon />
                <span className="text-sm">{message}</span>
              </div>
              <button onClick={() => sonnerToast.dismiss(t)} className="hover:bg-white/20 rounded p-0.5 transition-colors">
                <CloseIcon />
              </button>
            </div>
          )}
        </div>
      ),
      { duration: options?.duration }
    )
  },

  default: (message: string, options?: ToastOptions) => {
    return sonnerToast(message, options)
  },
}
